import React, { useState, useRef, useEffect } from 'react';
import { BrowserMultiFormatReader, Exception } from '@zxing/library';
import { useNavigate, useLocation } from 'react-router-dom';

const QRScanner = () => {
  const [scanning, setScanning] = useState(true);
  const [error, setError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const stopCurrentStream = () => {
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks();
      tracks.forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const resetScanner = () => {
    stopCurrentStream();
    if (codeReaderRef.current) {
      try {
        codeReaderRef.current.reset();
      } catch (e) {
        console.error('Errore durante il reset dello scanner:', e);
      }
      codeReaderRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    if (location.pathname === "/home" && !scanning) {
      setScanning(true);
    }

    const abortController = new AbortController();

    const initializeScanner = async () => {
      if (isInitializing) return;
      setIsInitializing(true);
      setError(null);

      try {
        resetScanner();
        codeReaderRef.current = new BrowserMultiFormatReader();

        const constraints = {
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (abortController.signal.aborted) {
          stopCurrentStream();
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        if (abortController.signal.aborted) {
          stopCurrentStream();
          return;
        }

        await codeReaderRef.current.decodeFromVideoElement(
          videoRef.current,
          (result) => {
            if (result && scanning) {
              setScanning(false);
              handleScannerSuccess(result.getText());
            }
          },
          (error) => {
            if (error && !(error instanceof Exception)) {
              console.error('Errore durante la scansione:', error);
            }
          }
        );
      } catch (err) {
        if (!abortController.signal.aborted) {
          console.error('Errore durante l\'inizializzazione:', err);
          setError(err.message || "Errore durante l'inizializzazione della fotocamera");
          navigate('/error', { 
            state: { message: "Errore nell'accesso alla fotocamera. Assicurati di aver dato i permessi necessari." } 
          });
        }
      } finally {
        setIsInitializing(false);
      }
    };

    if (scanning) {
      initializeScanner();
    }

    return () => {
      abortController.abort();
      resetScanner();
    };
  }, [navigate, scanning, location.pathname]);

  const handleScannerSuccess = (content) => {
    setScanning(false);
    // console.log("QR Content:", content); // Per debug
    checkUrl(content);
  };

  const checkUrl = (content) => {
    // console.log("QR Content:", content); // Per debug

    // Verifica se il contenuto è un URL valido
    try {
      const url = new URL(content);

      // Se l'URL contiene il nostro hostname, è valido
      if (url.hostname.includes(window.location.hostname)) {
        navigate(url.pathname);
      } else {
        navigate('/error', { state: { message: "Il QR code non è valido per questa app" } });
      }
    } catch (e) {
      // Se il contenuto non è un URL valido, vai alla pagina di errore
      navigate('/error', { state: { message: "Il contenuto del QR code non è valido" } });
    }
  };

  return (
    <div className="qr-scanner">
      <video ref={videoRef} />
      <div className="qr-overlay">
        <div className="qr-frame"></div>
      </div>
      <button onClick={() => setScanning(true)} className="btn btn-primary mt-3">
        Riprova scansione
      </button>
    </div>
  );
};

export default QRScanner;
